/**
 * コレクションフィルター（v2）の処理
 */

const MOBILE_BREAKPOINT = 1080;
const FILTER_INPUT_DEBOUNCE_MS = 250;
const MOBILE_COUNT_DEBOUNCE_MS = 300;

const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;
// スマホで全項目モーダルを開いているか（開いている間は「絞り込む」で反映、それ以外は選んだら即反映）
const isMobileModalOpen = () => isMobile() && !!document.getElementById("filter-v2-unit")?.classList.contains("active");

const getElement = (id) => document.getElementById(id);
const getElements = () => ({
  filterBtn: getElement("filter-v2-btn"),
  filterUnit: getElement("filter-v2-unit"),
  prevBtn: getElement("filter-v2-title-reset"),
  applyBtn: getElement("filter-v2-btn-apply"),
  featuredProductsSection: getElement("collection-featured-products"),
  featuredProductsIndex: getElement("collection-index-featured-products"),
  form: getElement("filter-v2-form"),
  loadingIcon: document.querySelector(".icon-svg-loading"),
  body: document.body,
  pageContainer: getElement("PageContainer"),
  productSortDom: getElement("filter-v2-products-sort-selectbox"),
  productCountEl: getElement("filter-v2-product-count"),
});

let elements = {};

const FILTER_NAME_MAPPING = {
  tag: "filter.p.tag",
  series: "filter.p.m.filter.series",
  functions: "filter.p.m.filter.functions",
  shape: "filter.p.m.filter.shape",
  type: "filter.p.m.filter.type",
  playTime: "filter.p.m.filter.play_time",
  scene: "filter.p.m.filter.scene",
  output: "filter.p.m.filter.output",
  capacity: "filter.p.m.filter.capacity",
  numberOfPorts: "filter.p.m.filter.number_of_ports",
  portTypes: "filter.p.m.filter.port_types",
  chargingTypeFunctions: "filter.p.m.filter.charging_type_functions",
  length: "filter.p.m.filter.length_multiple",
  kinds: "filter.p.m.filter.kinds",
  inputs: "filter.p.m.filter.inputs",
  acFixedOutput: "filter.p.m.filter.ac_fixed_output",
  batteryMaterial: "filter.p.m.filter.battery_material",
  stationType: "filter.p.m.filter.station_type",
  waterWipeType: "filter.p.m.filter.water_wipe_type",
  resolution: "filter.p.m.filter.resolution",
  vendor: "filter.p.vendor",
  luminance: "filter.p.m.filter.luminance",
};

/* ========================================
 * AbortController（fetch 競合制御）
 * ======================================== */

let currentFetchController = null;
let currentCountController = null;

const abortableFetch = (url, controller) =>
  fetch(url, {
    headers: { Accept: "text/html" },
    credentials: "same-origin",
    signal: controller.signal,
  });

/* ========================================
 * モーダル開閉（ch-plugin 非依存）
 * ======================================== */

const closeModal = (cancel = false) => {
  elements.filterUnit?.classList.remove("active");
  const chPlugin = getElement("ch-plugin");
  if (chPlugin) chPlugin.classList.remove("hide");
  elements.body.classList.remove("is-fixed");
  if (cancel) {
    setInitialValues();
    updateProductCountDisplay();
    applyFilterAvailability(readFilterCounts());
  }
  fxRender();
  updateClearLinkVisibility();
};

const displayModal = () => {
  const unit = elements.filterUnit;
  if (!unit) return;
  fxClosePop();
  const isOpening = !unit.classList.contains("active");
  unit.classList.toggle("active");
  const chPlugin = getElement("ch-plugin");
  if (chPlugin) chPlugin.classList.toggle("hide");
  elements.body.classList.toggle("is-fixed");
  if (isOpening) {
    setInitialValues();
    updateProductCountDisplay();
  }
  updateClearLinkVisibility();
};

/* ========================================
 * URL / フォームパラメータ
 * ======================================== */

const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const filterParams = urlParams.toString();
  return filterParams ? filterParams.split("&") : [];
};

const IGNORED_PARAMS = new Set(["type", "sort_by", "q"]);
const PRICE_DEFAULTS = { "filter.v.price.gte": "1", "filter.v.price.lte": "10000000" };
const PRICE_STEPS = [1, 2000, 4000, 5000, 7000, 10000, 15000, 20000, 30000, 50000, 70000, 100000, 150000, 200000, 300000, 10000000];

const isFilterParam = (key, value) => {
  if (key === "q") return value && value !== "*";
  if (key === "filter.v.availability") return false;
  if (key in PRICE_DEFAULTS) return value !== PRICE_DEFAULTS[key];
  if (key.startsWith("filter.p.") || key.startsWith("filter.v.")) return true;
  return false;
};

/**
 * URL ベースでフィルターが選択されているか
 */
const hasFilterParams = () => {
  const params = new URLSearchParams(window.location.search);
  for (const [key, value] of params) {
    if (isFilterParam(key, value)) return true;
  }
  return false;
};

/**
 * フォーム状態ベースでフィルターが選択されているか（スマホモーダル用）
 */
const hasFormFilterParams = () => {
  if (!elements.form) return false;
  const formData = new FormData(elements.form);
  for (const [key, value] of formData) {
    if (isFilterParam(key, value)) return true;
  }
  return false;
};

/**
 * 条件をクリアリンクの表示/非表示を更新
 */
const updateClearLinkVisibility = () => {
  const clearElements = document.querySelectorAll(
    ".filter-v2-link-clear, .filter-v2-clear-pc"
  );
  const isModalOpen = elements.filterUnit?.classList.contains("active");
  const show = isModalOpen && isMobile() ? hasFormFilterParams() : hasFilterParams();
  clearElements.forEach((el) => {
    el.classList.toggle("filter-v2-clear-hidden", !show);
  });
};

/* ========================================
 * アクティブフィルターのチップ表示
 * ======================================== */

const TAG_DISPLAY_MAP = {
  sale: "セール対象製品",
  has_privilege: "購入特典あり",
  new_iphone: "新型iPhoneにおすすめ",
  eol: "販売終了",
  new: "新製品",
  popular_product: "人気製品",
  bulk_cp: "まとめ買い対象",
};

const getChipDisplayValue = (key, rawValue) => {
  const decoded = decodeURIComponent(rawValue);
  if (key === "filter.p.tag" && TAG_DISPLAY_MAP[decoded]) {
    return TAG_DISPLAY_MAP[decoded];
  }
  if (key.startsWith("filter.v.price")) {
    return `¥${Number(rawValue).toLocaleString()}`;
  }
  return decoded;
};

const renderActiveChips = () => {
  const container = getElement("filter-v2-active-chips");
  if (!container) return;

  const params = new URLSearchParams(window.location.search);
  const chips = [];

  for (const [key, value] of params) {
    if (key === "filter.v.availability") continue;
    if (!isFilterParam(key, value)) continue;
    chips.push({ key, value, text: getChipDisplayValue(key, value) });
  }

  if (chips.length === 0) {
    container.innerHTML = "";
    return;
  }

  const baseUrl = new URL(window.location.href);
  const chipHtml = chips
    .map((chip) => {
      const removeUrl = new URL(baseUrl);
      const newParams = new URLSearchParams(removeUrl.search);
      const allValues = newParams.getAll(chip.key);
      newParams.delete(chip.key);
      allValues
        .filter((v) => v !== chip.value)
        .forEach((v) => newParams.append(chip.key, v));
      removeUrl.search = newParams.toString();
      return `<a href="${removeUrl.toString()}" class="filter-v2-chip" data-chip-url="${removeUrl.toString()}"><span class="filter-v2-chip-text">${chip.text}</span><span class="filter-v2-chip-remove">×</span></a>`;
    })
    .join("");

  container.innerHTML = chipHtml;
};

/* ========================================
 * 商品件数の表示
 * ======================================== */

const getCountContainer = (doc = document) =>
  doc.querySelector(".collection-products-v2-inner") ??
  doc.querySelector(".search-v2-inner");

const updateProductCountDisplay = () => {
  const inner = getCountContainer();
  const count = inner?.getAttribute("data-product-count") ?? "0";
  if (elements.productCountEl) {
    elements.productCountEl.textContent = `（${count}件）`;
  }
};

const fetchAndUpdateProductCount = async (url) => {
  const countEl = elements.productCountEl;
  if (!countEl) return;

  if (currentCountController) currentCountController.abort();
  currentCountController = new AbortController();

  countEl.classList.add("is-loading");
  try {
    const response = await abortableFetch(url, currentCountController);
    if (!response.ok) return;
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const inner = getCountContainer(doc);
    const count = inner?.getAttribute("data-product-count") ?? "0";
    applyFilterAvailability(readFilterCounts(doc));
    countEl.classList.remove("is-loading");
    countEl.textContent = `（${count}件）`;
    countEl.classList.add("is-updated");
    countEl.addEventListener(
      "animationend",
      () => countEl.classList.remove("is-updated"),
      { once: true }
    );
  } catch (e) {
    if (e.name !== "AbortError") countEl.classList.remove("is-loading");
  }
};

/* ========================================
 * 0 件になる選択肢をグレーにする
 * 一覧と一緒に返ってくる .filter-v2-counts（snippets/filter-v2-counts.liquid）の件数を使う。
 * 件数は Shopify の「今の条件にその値を足したら何件か」。同じ項目内（出力など）は自分の項目を除いて数えるので、
 * ラジオで切り替えたときの件数と一致する。0 の選択肢は押せなくする（選択中のものは外せるように残す）。
 * ======================================== */

const readFilterCounts = (doc = document) => {
  const el = getCountContainer(doc)?.querySelector(".filter-v2-counts");
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
};

// Shopify が返す値の一覧は 1 項目 100 件まで（タグは 100 を超えて打ち切られる）。打ち切られた項目に無い値は「不明」
const FILTER_VALUES_LIMIT = 100;
let availabilitySeq = 0;

const setUnavailable = (input, off) => {
  input.disabled = off;
  input.closest(".filter-v2-group")?.classList.toggle("is-unavailable", off);
};

const updateBlockAvailability = () => {
  // 項目の中が全部 0 件なら見出しも薄くする
  elements.form.querySelectorAll(".filter-v2-block").forEach((block) => {
    const groups = block.querySelectorAll(".filter-v2-group");
    const offs = block.querySelectorAll(".filter-v2-group.is-unavailable");
    block.classList.toggle("is-unavailable", groups.length > 0 && groups.length === offs.length);
  });
};

/**
 * 件数が不明な選択肢は、その値を足した URL を section_id 付きで取りに行き（一覧のセクションだけ返る）件数を確かめる
 */
const resolveUnknownAvailability = async (inputs, seq) => {
  const sectionId = getCountContainer()?.closest("[id^='shopify-section-']")?.id.replace("shopify-section-", "");
  if (!sectionId) return;
  await Promise.all(
    inputs.map(async (input) => {
      // ラジオは選ぶと同じ項目の選択が外れるので、URL を作ったら元に戻す
      const prev = input.type === "radio" ? elements.form.querySelector(`input[name="${input.name}"]:checked`) : null;
      // 押せない（disabled）ままだとフォームの値に入らないので、URL を作る間だけ有効にする
      const wasDisabled = input.disabled;
      input.disabled = false;
      input.checked = true;
      const url = createFilterUrl(elements.form);
      input.checked = false;
      input.disabled = wasDisabled;
      if (prev) prev.checked = true;
      try {
        const res = await fetch(`${url}&section_id=${encodeURIComponent(sectionId)}`, { credentials: "same-origin" });
        if (!res.ok || seq !== availabilitySeq) return;
        const m = (await res.text()).match(/data-product-count="(\d+)"/);
        if (seq === availabilitySeq) setUnavailable(input, !!m && m[1] === "0" && !input.checked);
      } catch {
        /* 取れなければ押せるようにしておく */
        if (seq === availabilitySeq) setUnavailable(input, false);
      }
    })
  );
  if (seq === availabilitySeq) {
    updateBlockAvailability();
    fxRender();
  }
};

const paramName = (input) => input.name.replace(/\.color$/, "");

const applyFilterAvailability = (counts) => {
  if (!elements.form || !counts) return;
  const seq = ++availabilitySeq;
  const unknown = [];
  const inputs = [...elements.form.querySelectorAll("input[type='checkbox'], input[type='radio']")].filter(
    // filter.v.*（在庫・価格）と「販売終了」（押すと在庫ありを外す）は対象外
    (input) => input.name.startsWith("filter.p.") && input.id !== "eol"
  );
  const selectedParams = new Set(inputs.filter((i) => i.checked).map(paramName));
  inputs.forEach((input) => {
    const values = counts[paramName(input)];
    // Shopify 側に無い項目は判断できないので触らない
    if (!values) return setUnavailable(input, false);
    if (input.checked) return setUnavailable(input, false);
    let count = values[input.value];
    if (count === undefined && Object.keys(values).length < FILTER_VALUES_LIMIT) count = 0;
    if (count === undefined) {
      // 打ち切られて一覧に無い値: 確かめるまでは押せるままにする
      setUnavailable(input, false);
      unknown.push(input);
    } else if (count === 0 && selectedParams.has(paramName(input))) {
      // 同じ項目に選択があるときの件数は、項目によって「足した件数」でなく「今の結果の中の件数」で返る（長さなど OR の項目）
      // → 0 のときだけ実際に確かめる。確かめるまではグレーのまま
      setUnavailable(input, true);
      unknown.push(input);
    } else {
      setUnavailable(input, count === 0);
    }
  });
  updateBlockAvailability();
  fxRender();
  if (unknown.length) resolveUnknownAvailability(unknown, seq);
};

/* ========================================
 * フォーム初期値設定
 * ======================================== */

const replaceInputValue = (text) =>
  text.includes("+") ? text.replaceAll("+", " ") : text;

const findMatchedData = (paramValues, filterName, inputValue) =>
  paramValues.find(
    (param) =>
      param.name.includes(filterName) &&
      replaceInputValue(param.value) === inputValue
  );

const setInputInitialValues = (inputs, paramValues) => {
  inputs.forEach((input) => {
    const inputName = input.getAttribute("name");
    if (!inputName) return;

    if (input.type === "checkbox" || input.type === "radio") {
      input.checked = false;
    }

    if (inputName.includes("filter.p.tag")) {
      const tagMatchedData = findMatchedData(paramValues, "filter.p.tag", input.value);
      if (tagMatchedData) {
        input.checked = true;
        return;
      }
    }

    const paramData = paramValues.find((param) => param.name === inputName);
    if (paramData) {
      if (inputName === "filter.v.availability" && paramData.value === "1") {
        input.checked = true;
      } else if (inputName === "sort_by" && input.value === paramData.value) {
        input.checked = true;
      } else if (inputName === "q") {
        input.value = paramData.value;
      }
    }

    Object.values(FILTER_NAME_MAPPING).forEach((filterName) => {
      if (inputName === filterName) {
        const matchedData = findMatchedData(paramValues, filterName, input.value);
        if (matchedData) input.checked = true;
      }
    });
  });
};

/* ========================================
 * Price Range Slider
 * ======================================== */

const formatPriceLabel = (value) => {
  if (value <= 1) return "下限なし";
  if (value >= 10000000) return "上限なし";
  return `¥${value.toLocaleString()}`;
};

const priceToStep = (price) => {
  const num = Number(price);
  let closest = 0;
  let minDiff = Infinity;
  for (let i = 0; i < PRICE_STEPS.length; i++) {
    const diff = Math.abs(PRICE_STEPS[i] - num);
    if (diff < minDiff) { minDiff = diff; closest = i; }
  }
  return closest;
};

const updatePriceSliderUI = (container) => {
  const minInput = container.querySelector(".price-slider-input-min");
  const maxInput = container.querySelector(".price-slider-input-max");
  if (!minInput || !maxInput) return;

  let minStep = parseInt(minInput.value);
  let maxStep = parseInt(maxInput.value);

  if (minStep > maxStep) {
    if (minInput === document.activeElement) {
      minInput.value = maxStep;
      minStep = maxStep;
    } else {
      maxInput.value = minStep;
      maxStep = minStep;
    }
  }

  const range = container.querySelector(".price-slider-range");
  const labelMin = container.querySelector(".price-slider-label-min");
  const labelMax = container.querySelector(".price-slider-label-max");
  const hiddenGte = container.querySelector('input[name="filter.v.price.gte"]');
  const hiddenLte = container.querySelector('input[name="filter.v.price.lte"]');
  const last = PRICE_STEPS.length - 1;
  const leftPct = (minStep / last) * 100;
  const rightPct = (maxStep / last) * 100;

  if (range) { range.style.left = leftPct + "%"; range.style.width = (rightPct - leftPct) + "%"; }
  if (labelMin) labelMin.textContent = formatPriceLabel(PRICE_STEPS[minStep]);
  if (labelMax) labelMax.textContent = formatPriceLabel(PRICE_STEPS[maxStep]);
  if (hiddenGte) hiddenGte.value = PRICE_STEPS[minStep];
  if (hiddenLte) hiddenLte.value = PRICE_STEPS[maxStep];
};

const setPriceSliderInitialValues = () => {
  const container = getElement("filter-v2-price-range");
  if (!container) return;
  const minInput = container.querySelector(".price-slider-input-min");
  const maxInput = container.querySelector(".price-slider-input-max");
  if (!minInput || !maxInput) return;

  const params = new URLSearchParams(window.location.search);
  const gteValue = params.get("filter.v.price.gte");
  const lteValue = params.get("filter.v.price.lte");
  minInput.value = gteValue ? priceToStep(gteValue) : 0;
  maxInput.value = lteValue ? priceToStep(lteValue) : PRICE_STEPS.length - 1;
  updatePriceSliderUI(container);
};

let priceSliderDebounceTimer = null;

const initPriceSlider = () => {
  const container = getElement("filter-v2-price-range");
  if (!container) return;
  const minInput = container.querySelector(".price-slider-input-min");
  const maxInput = container.querySelector(".price-slider-input-max");
  if (!minInput || !maxInput) return;

  setPriceSliderInitialValues();

  const onInput = () => {
    let minVal = parseInt(minInput.value);
    let maxVal = parseInt(maxInput.value);
    if (minVal > maxVal) {
      if (minInput === document.activeElement) { minInput.value = maxVal; }
      else { maxInput.value = minVal; }
    }
    updatePriceSliderUI(container);
  };

  const onChange = () => {
    updateClearLinkVisibility();
    if (isMobileModalOpen()) {
      triggerMobileCountUpdate();
    } else {
      clearTimeout(priceSliderDebounceTimer);
      priceSliderDebounceTimer = setTimeout(() => {
        executeFilterSearch(elements.form);
      }, FILTER_INPUT_DEBOUNCE_MS);
    }
  };

  minInput.addEventListener("input", onInput);
  maxInput.addEventListener("input", onInput);
  minInput.addEventListener("change", onChange);
  maxInput.addEventListener("change", onChange);
};

const setInitialValues = () => {
  if (!elements.form) return;
  const inputs = elements.form.querySelectorAll("input");
  const urlParams = getUrlParams();
  const paramValues = urlParams.map((param) => {
    const [name, value] = param.split("=");
    return { name, value: decodeURIComponent(value || "") };
  });

  setInputInitialValues(inputs, paramValues);
  setPriceSliderInitialValues();

  const sortByParam = paramValues.find((param) => param.name === "sort_by");
  const sortDom = getElement("filter-v2-products-sort-selectbox");
  if (sortDom) {
    if (sortByParam) {
      sortDom.value = sortByParam.value;
    } else {
      const firstOption = sortDom.querySelector("option");
      if (firstOption) sortDom.value = firstOption.value;
    }
  }
};

/* ========================================
 * おすすめ商品 / スクロール
 * ======================================== */

const hideFeaturedProducts = () => {
  const urlParams = getUrlParams();
  const url = location.href;
  if (urlParams.length === 0 || !url.includes("filter")) return;
  elements.featuredProductsSection?.classList.add("hide");
  elements.featuredProductsIndex?.classList.add("hide");
};

/* ========================================
 * フィルター URL 生成
 * ======================================== */

const createFilterUrl = (formData) => {
  const url = window.location.pathname;
  const form = new FormData(formData);

  const stockToggle = getElement("filter-v2-stock-toggle");
  if (stockToggle?.checked) {
    form.set("filter.v.availability", "1");
  } else {
    form.delete("filter.v.availability");
  }

  let params = new URLSearchParams(form);
  const query = params.get("q");

  if (!query && url.includes("/search")) {
    form.set("q", "*");
    params = new URLSearchParams(form);
  }

  let urlParams = params.toString();
  // カラーは filter.p.tag.color で持っているので filter.p.tag に直す（2 色目以降も。replace だと 1 つ目しか直らず 2 色目が無視されていた）
  urlParams = urlParams.replaceAll(".color", "");
  return `${url}?${urlParams}`;
};

/* ========================================
 * ローディング（部分 fade）
 * ======================================== */

const showLoading = () => {
  const inner = document.querySelector(".collection-products-v2-inner") ??
    document.querySelector(".search-v2-inner");
  if (inner) inner.classList.add("is-loading");
  if (elements.loadingIcon) elements.loadingIcon.classList.add("active");
};

const hideLoading = () => {
  const inner = document.querySelector(".collection-products-v2-inner") ??
    document.querySelector(".search-v2-inner");
  if (inner) inner.classList.remove("is-loading");
  if (elements.loadingIcon) elements.loadingIcon.classList.remove("active");
};

/* ========================================
 * Fetch & 部分更新
 * ======================================== */

const refreshDynamicElements = () => {
  const newSort = getElement("filter-v2-products-sort-selectbox");
  if (newSort && newSort !== elements.productSortDom) {
    newSort.addEventListener("change", handleSortChange);
  }
  elements.productSortDom = newSort;

  const newFilterBtn = getElement("filter-v2-btn");
  if (newFilterBtn && newFilterBtn !== elements.filterBtn) {
    newFilterBtn.addEventListener("click", displayModal);
  }
  elements.filterBtn = newFilterBtn;

  elements.productCountEl = getElement("filter-v2-product-count");
  initStockToggle();

  if (window.themeV2?.resetProductCardImages) {
    window.themeV2.resetProductCardImages();
  }
  if (window.themeV2?.setSwipeProductCardImage) {
    window.themeV2.setSwipeProductCardImage();
  }
};

const fetchAndReplace = async (url, opts = {}) => {
  if (currentFetchController) currentFetchController.abort();
  currentFetchController = new AbortController();

  try {
    const response = await abortableFetch(url, currentFetchController);
    if (!response.ok) throw new Error("Fetch failed");
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    // コレクション・検索とも一覧だけ差し替える（検索は以前ここで見つからずページごと読み直していた）
    const newInner = getCountContainer(doc);
    const currentInner = getCountContainer();
    if (newInner && currentInner) {
      currentInner.replaceWith(document.importNode(newInner, true));
      if (!opts.skipPushState) {
        // 初期適用は履歴を積まない（戻るで絞り込み前の状態に戻ってしまうため）
        if (opts.replaceState) {
          history.replaceState(null, "", url);
        } else {
          history.pushState(null, "", url);
        }
      }
      refreshDynamicElements();
      fxAfterFetch();
      setInitialValues();
      applyFilterAvailability(readFilterCounts());
      syncStockToggle();
      renderActiveChips();
      updateProductCountDisplay();
      hideFeaturedProducts();
      updateClearLinkVisibility();
    } else {
      window.location.href = url;
    }
  } catch (e) {
    if (e.name === "AbortError") return;
    window.location.href = url;
  } finally {
    hideLoading();
  }
};

/* ========================================
 * フィルター操作
 * ======================================== */

const handleSortChange = (e) => {
  showLoading();
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set("sort_by", e.target.value);
  // 並び替えると各ページの中身が変わるため 1 ページ目に戻す
  currentUrl.searchParams.delete("page");
  fetchAndReplace(currentUrl.toString());
};

const executeFilterSearch = (formData) => {
  showLoading();
  const url = createFilterUrl(formData);
  fetchAndReplace(url);
};

let filterInputDebounceTimer = null;

const handleFilterInput = () => {
  if (isMobile()) return;
  clearTimeout(filterInputDebounceTimer);
  filterInputDebounceTimer = setTimeout(() => {
    executeFilterSearch(elements.form);
  }, FILTER_INPUT_DEBOUNCE_MS);
};

let mobileCountDebounceTimer = null;

const triggerMobileCountUpdate = () => {
  if (!isMobile() || !elements.filterUnit?.classList.contains("active")) return;
  clearTimeout(mobileCountDebounceTimer);
  mobileCountDebounceTimer = setTimeout(() => {
    const url = createFilterUrl(elements.form);
    fetchAndUpdateProductCount(url);
  }, MOBILE_COUNT_DEBOUNCE_MS);
};

const handleFilterChange = (e) => {
  const target = e.target;
  if (target.matches("input[type='text']")) {
    handleFilterInput();
    if (isMobile()) triggerMobileCountUpdate();
    return;
  }
  if (target.matches("input[type='checkbox'], input[type='radio'], select")) {
    if (target.id === "eol") {
      const stockToggle = getElement("filter-v2-stock-toggle");
      if (stockToggle) stockToggle.checked = !target.checked;
    }
    updateClearLinkVisibility();
    if (isMobileModalOpen()) {
      triggerMobileCountUpdate();
    } else {
      executeFilterSearch(elements.form);
    }
    fxRender();
  }
};

/**
 * radio のデセレクト対応：選択済み radio をクリックで解除
 */
let _radioClickTarget = null;
let _radioWasChecked = false;

const handleRadioMousedown = (e) => {
  const group = e.target.closest(".filter-v2-group");
  const radio = group?.querySelector('input[type="radio"]');
  if (radio) {
    _radioClickTarget = radio;
    _radioWasChecked = radio.checked;
  } else {
    _radioClickTarget = null;
  }
};

const handleRadioClick = (e) => {
  const group = e.target.closest(".filter-v2-group");
  const radio = group?.querySelector('input[type="radio"]');
  if (radio && radio === _radioClickTarget && _radioWasChecked) {
    const uncheck = () => {
      radio.checked = false;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    };
    if (e.target === radio) {
      // ラジオ本体: クリックの既定動作が終わってから外す（先に外すと既定動作で選択し直される）
      setTimeout(uncheck, 0);
    } else {
      // 文字（label）: label の既定動作がラジオを選択し直すので止めてから外す
      e.preventDefault();
      uncheck();
    }
  }
  _radioClickTarget = null;
  _radioWasChecked = false;
};

/* ========================================
 * クリアリンクの fetch 対応
 * ======================================== */

const buildClearUrl = (baseHref) => {
  const url = new URL(baseHref, window.location.origin);
  const stockToggle = getElement("filter-v2-stock-toggle");
  if (stockToggle?.checked) {
    url.searchParams.set("filter.v.availability", "1");
  }
  return url.toString();
};

const handleClearLinkClick = (e) => {
  const link = e.target.closest("a.filter-v2-link-clear, a.filter-v2-link-clear-pc");
  if (!link) return;
  const href = link.getAttribute("href");
  if (!href) return;

  e.preventDefault();
  const clearUrl = buildClearUrl(href);

  if (isMobile() && elements.filterUnit?.classList.contains("active")) {
    closeModal(false);
    setTimeout(() => {
      showLoading();
      fetchAndReplace(clearUrl);
    }, 0);
    return;
  }

  showLoading();
  fetchAndReplace(clearUrl);
};

/* ========================================
 * PC サイドバー アコーディオン
 * ======================================== */

const initAccordion = () => {
  if (isMobile()) return;
  const form = elements.form;
  if (!form) return;
  form.querySelectorAll(".filter-v2-block-title").forEach((title) => {
    title.addEventListener("click", () => {
      if (isMobile()) return;
      title.closest(".filter-v2-block")?.classList.toggle("is-collapsed");
    });
  });
};

/* ========================================
 * 在庫ありトグル
 * ======================================== */

const syncStockToggle = () => {
  const toggle = getElement("filter-v2-stock-toggle");
  if (!toggle) return;
  const params = new URLSearchParams(window.location.search);
  const isSearch = window.location.pathname.includes("/search");
  if (isSearch && !params.has("filter.v.availability")) {
    toggle.checked = true;
  } else {
    toggle.checked = params.get("filter.v.availability") === "1";
  }
};

const handleStockToggleChange = () => {
  const toggle = getElement("filter-v2-stock-toggle");
  if (!toggle) return;
  const currentUrl = new URL(window.location.href);
  if (toggle.checked) {
    currentUrl.searchParams.set("filter.v.availability", "1");
  } else {
    currentUrl.searchParams.delete("filter.v.availability");
  }
  // 絞り込みで総ページ数が減ると現在のページ番号が範囲外になるため 1 ページ目に戻す
  currentUrl.searchParams.delete("page");
  showLoading();
  fetchAndReplace(currentUrl.toString());
};

const initStockToggle = () => {
  const toggle = getElement("filter-v2-stock-toggle");
  if (!toggle) return;
  // 最初の状態は snippets/filter-v2-title が描画済み。パラメータ無しのコレクションで URL から読み直すと、
  // applyDefaultStockFilter の適用前に一瞬 OFF に戻ってしまうので、そのときは読み直さない
  const path = window.location.pathname;
  const isDefaultPending = path.startsWith("/collections/") && path !== "/collections/" && !new URLSearchParams(window.location.search).has("filter.v.availability");
  if (!isDefaultPending) syncStockToggle();
  toggle.removeEventListener("change", handleStockToggleChange);
  toggle.addEventListener("change", handleStockToggleChange);
};

/**
 * コレクションページを filter.v.availability 無しで開いた場合に「在庫ありのみ」を初期適用する。
 *
 * サイト内リンク経由は layout/theme.liquid のリンク書き換えでクリック時にパラメータが付くため、
 * ここを通るのは URL 直打ち・ブックマーク・検索エンジン・外部リンクからの初回流入のみ。
 * つまり通常の回遊では追加リクエストは発生しない。
 */
const applyDefaultStockFilter = () => {
  const path = window.location.pathname;
  if (!path.startsWith("/collections/") || path === "/collections/") return;

  const url = new URL(window.location.href);
  if (url.searchParams.has("filter.v.availability")) return;
  url.searchParams.set("filter.v.availability", "1");
  // 絞り込みで総ページ数が減るため、範囲外のページ番号にならないよう1ページ目に戻す
  url.searchParams.delete("page");

  showLoading();
  fetchAndReplace(url.toString(), { replaceState: true });
};

/* ========================================
 * スマホ: チップで絞り込む（2026-10）
 * 全画面のモーダルだと一覧が隠れるので、一覧の上に項目ごとのチップを並べ、押した項目だけ小窓で開く。
 * 選んだら即反映（一覧が見えたまま入れ替わる）。主役の項目（カテゴリ専用の先頭の項目）は選択肢を最初から並べる。
 * 先頭の「絞り込み」チップは従来の全項目モーダルを開く。フォームは PC・モーダルと共通（#filter-v2-form）。
 * ======================================== */

const fx = { bar: null, main: null, row: null, foot: null, openBlock: null, primary: null, raf: 0 };

const fxBlocks = () =>
  [...(elements.form?.querySelectorAll(".filter-v2-block") ?? [])].filter(
    (b) => b.querySelector("input[type='checkbox'], input[type='radio'], input[type='range']") && b.id !== "filter-v2-stock"
  );
const fxTitle = (block) => block.querySelector(".filter-v2-block-title")?.textContent.trim() ?? "";
const fxShort = (text) => text.replace(/\s*[(（].*[)）]\s*$/, "").trim();
const fxEsc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const fxInputs = (block) => [...block.querySelectorAll("input[type='checkbox'], input[type='radio']")];
// 選択肢の文字（後から足した件数 .fx-n は除く）
const fxLabel = (input) => {
  const text = input.closest(".filter-v2-group")?.querySelector(".filter-v2-group-text");
  if (!text) return input.value;
  return [...text.childNodes].filter((n) => !(n.nodeType === 1 && n.classList.contains("fx-n"))).map((n) => n.textContent).join("").trim();
};

// 選んだら何件になるか（Shopify の件数）。同じ項目に選択があるときは当てにならないので出さない
const fxCount = (input, counts, selected) => {
  if (!counts || input.checked || input.disabled) return null;
  const name = input.name.replace(/\.color$/, "");
  if (selected.has(name)) return null;
  const n = counts[name]?.[input.value];
  return typeof n === "number" && n > 0 ? n : null;
};

const fxSummary = (block) => {
  if (block.id === "filter-v2-price-range") {
    const params = new URLSearchParams(window.location.search);
    const gte = params.get("filter.v.price.gte");
    const lte = params.get("filter.v.price.lte");
    const on = (gte && gte !== "1") || (lte && lte !== "10000000");
    return on ? `${formatPriceLabel(Number(gte || 1))}〜${formatPriceLabel(Number(lte || 10000000))}` : "";
  }
  const on = fxInputs(block).filter((i) => i.checked);
  if (!on.length) return "";
  return on.length === 1 ? fxShort(fxLabel(on[0])) : `${fxTitle(block)} ${on.length}`;
};

// シートの中の「並び替え」: 元のプルダウン（一覧と一緒に描き直される）を中継して操作する
const fxSyncSort = () => {
  const unit = elements.filterUnit;
  const sel = elements.productSortDom;
  if (!unit || !sel) return;
  let box = unit.querySelector(".fx-sort");
  if (!box) {
    box = document.createElement("div");
    box.className = "fx-sort";
    elements.form?.prepend(box);
  }
  box.innerHTML =
    `<p class="fx-sort-t">並び替え</p><div class="fx-sort-row">` +
    [...sel.options].map((o, i) => `<button type="button" class="fx-sort-b${i === sel.selectedIndex ? " is-on" : ""}" data-fx-sort="${fxEsc(o.value)}" aria-pressed="${i === sel.selectedIndex}">${fxEsc(o.textContent.trim())}</button>`).join("") +
    `</div>`;
};

const fxSetTop = () => {
  // ヘッダーはスクロールで出入りする（top: -100% ⇔ 0）。見えている下端にチップを付ける
  const header = document.querySelector(".js-v2-header-sticky, .v2-header-sticky");
  const bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  document.documentElement.style.setProperty("--fx-top", `${bottom}px`);
  if (fx.bar && fx.openBlock) {
    const r = fx.bar.getBoundingClientRect();
    document.documentElement.style.setProperty("--fx-pop-top", `${Math.max(0, r.bottom)}px`);
  }
};
const fxTrackTop = () => {
  // ヘッダーの出入り（0.2 秒）のあいだも追う
  cancelAnimationFrame(fx.raf);
  const until = performance.now() + 350;
  const tick = () => {
    fxSetTop();
    if (performance.now() < until) fx.raf = requestAnimationFrame(tick);
  };
  tick();
};

const fxRender = () => {
  if (!fx.bar || !elements.form) return;
  // 主役の項目は件数で選ぶので、まだ何も絞り込んでいない間は件数が変わるたびに選び直す
  // （初回は在庫切れ込みの件数で判定され、直後に「在庫ありのみ」が自動で付くと結果が変わるため）
  if (![...elements.form.querySelectorAll("input[name^='filter.p.']:checked")].some((i) => i.id !== "eol")) {
    const next = fxPickPrimary(fxBlocks());
    if (next !== fx.primary) {
      fx.primary?.classList.remove("is-fx-primary");
      fx.primary = next;
      fx.primary?.classList.add("is-fx-primary");
      if (fx.main) fx.main.hidden = !fx.primary;
    }
  }
  const counts = readFilterCounts();
  const blocks = fxBlocks();
  const selected = new Set(
    [...elements.form.querySelectorAll("input:checked")].map((i) => i.name.replace(/\.color$/, ""))
  );
  const nSel = [...elements.form.querySelectorAll("input[name^='filter.p.']:checked")].filter((i) => i.id !== "eol").length;

  /* 10/8 C 案: 主役の項目は大きな選択肢カード（問いの見出しは付けない）、ほかの条件と並び替えはシート 1 つ、スクロールすると細いバーが付いてくる */
  const total = getCountContainer()?.getAttribute("data-product-count") ?? "";
  const sortSel = elements.productSortDom;
  const sortName = sortSel ? sortSel.options[sortSel.selectedIndex]?.textContent.trim() ?? "" : "";
  const sortOn = sortSel && sortSel.selectedIndex > 0;
  const nOther = [...elements.form.querySelectorAll("input[name^='filter.p.']:checked")].filter((i) => i.id !== "eol" && !(fx.primary && fx.primary.contains(i))).length + (sortOn ? 1 : 0);
  const pSum = fx.primary ? fxSummary(fx.primary) : "";
  fx.bar.innerHTML =
    `<b class="fx-mini-v">${fxEsc(pSum || (fx.primary ? `すべての${fxTitle(fx.primary)}` : "すべて"))}・<span>${fxEsc(total)}</span> 件</b>` +
    `<button type="button" class="fx-mini-btn" data-fx-all>条件を変える${nOther ? `（${nOther}）` : ""}</button>`;

  if (fx.primary) {
    // 該当 0 件の選択肢はカードにしない。選べるものが 1 つ以下なら（カテゴリ別ページで種類がすでに決まっている等）カードごと出さない
    const vals = fxInputs(fx.primary);
    const live = vals.map((i, idx) => [i, idx]).filter(([i]) => i.checked || !i.disabled);
    fx.main.hidden = live.length < 2 && !live.some(([i]) => i.checked);
    fx.main.innerHTML = fx.main.hidden ? "" :
      `<div class="fx-cards" data-n="${live.length}">` +
      live
        .map(([i, idx]) => {
          const n = fxCount(i, counts, selected);
          const full = fxLabel(i);
          const desc = (full.match(/[(（](.*)[)）]\s*$/) || [, ""])[1].replace(/に対応$/, "");
          return `<button type="button" class="fx-card${i.checked ? " is-on" : ""}" data-fx-pill="${idx}" aria-pressed="${i.checked}"${i.disabled ? " disabled" : ""}><b>${fxEsc(fxShort(full))}</b>${desc ? `<span>${fxEsc(desc)}</span>` : ""}${n ? `<small>${n} 件</small>` : ""}</button>`;
        })
        .join("") +
      `</div>`;
  }
  // 在庫ありのみは「ほかの条件」に混ぜず、件数の行に単独で出す（クリアで外れる条件と見分けがつくように。元のスイッチは中継）
  const stock = getElement("filter-v2-stock-toggle");
  fx.row.innerHTML =
    `<span class="fx-row-n"><b>${fxEsc(total)}</b> 件${sortName ? `・${fxEsc(sortName)}` : ""}</span>` +
    (stock ? `<button type="button" class="fx-stock" role="switch" aria-checked="${stock.checked}" data-fx-stock><span>在庫ありのみ</span><i></i></button>` : "") +
    `<button type="button" class="fx-row-btn" data-fx-all>ほかの条件・並び替え${nOther ? `（${nOther}）` : ""}</button>`;
  fxSyncSort();

  // 選択肢の横の件数（スマホの小窓・モーダルだけ CSS で表示）
  blocks.forEach((b) =>
    fxInputs(b).forEach((i) => {
      const text = i.closest(".filter-v2-group")?.querySelector(".filter-v2-group-text");
      if (!text) return;
      let el = text.querySelector(".fx-n");
      const n = fxCount(i, counts, selected);
      if (!n) return el?.remove();
      if (!el) {
        el = document.createElement("span");
        el.className = "fx-n";
        text.appendChild(el);
      }
      el.textContent = n;
    })
  );

  if (fx.openBlock && fx.foot) {
    const total = getCountContainer()?.getAttribute("data-product-count") ?? "0";
    fx.foot.innerHTML = `<button type="button" class="fx-pop-reset" data-fx-reset${fxSummary(fx.openBlock) ? "" : " hidden"}>${fxEsc(fxTitle(fx.openBlock))}を解除</button><button type="button" class="fx-pop-done" data-fx-close>${total}件を見る</button>`;
  }
};

const fxOpenPop = (block) => {
  const unit = elements.filterUnit;
  if (!unit) return;
  fx.openBlock?.classList.remove("is-fx-target");
  fx.openBlock = block;
  block.classList.add("is-fx-target");
  unit.classList.add("is-fx-pop");
  fxSetTop();
  unit.scrollTop = 0;
  fxRender();
};

const fxClosePop = () => {
  if (!fx.openBlock) return;
  fx.openBlock.classList.remove("is-fx-target");
  fx.openBlock = null;
  elements.filterUnit?.classList.remove("is-fx-pop");
  fxRender();
};

const fxResetBlock = (block) => {
  if (block.id === "filter-v2-price-range") {
    const min = block.querySelector(".price-slider-input-min");
    const max = block.querySelector(".price-slider-input-max");
    if (min && max) {
      min.value = 0;
      max.value = PRICE_STEPS.length - 1;
      updatePriceSliderUI(block);
    }
  } else {
    fxInputs(block).forEach((i) => (i.checked = false));
  }
  updateClearLinkVisibility();
  executeFilterSearch(elements.form);
  fxRender();
};

const fxAfterFetch = () => {
  // 一覧の途中までスクロールしていたら、入れ替わった一覧の先頭が見える位置まで戻す（小窓は開いたまま）。
  // 一覧が短くなるとチップの貼り付きも外れるので、チップ本来の位置（主役の別枠の直上）を基準にする
  if (!isMobile() || !fx.bar) return;
  const inner = getCountContainer();
  if (!inner) return;
  const barH = fx.bar.offsetHeight;
  if (inner.getBoundingClientRect().top >= fx.bar.getBoundingClientRect().bottom && fx.bar.getBoundingClientRect().top >= 0) return;
  // 上へ戻すとヘッダーが出てくる（テーマの動き）ので、その高さぶんも空ける
  const headerH = document.querySelector(".js-v2-header-sticky, .v2-header-sticky")?.offsetHeight ?? 0;
  const anchor = fx.main.getBoundingClientRect().top + window.scrollY - barH - headerH;
  if (window.scrollY > anchor) window.scrollTo({ top: anchor, behavior: "instant" });
  fxTrackTop();
};

/* 主役（大きなカード）にする項目を選ぶ。
 * 項目の並びは商品データの分類（充電器・ケーブル…）ごとの snippet で共通なので、先頭をそのまま使うと
 * 電源タップに「出力」、ワイヤレス充電器にも「出力」のように、そのページで選ぶ軸とずれる。
 * 1) FX_PRIMARY に書いたページはその項目（null はカードを出さない）
 * 2) それ以外は、カテゴリ専用の項目のうち「2 つ以上に分かれて、一覧の 7 割以上が どれか 1 つに入る」最初の項目。
 *    データの入っていない商品が多い項目や、1 商品が複数に入る項目（機能など）はカードに向かないので外す
 *    ただし ページ上部のサブカテゴリの行（USB-C & USB-C ケーブル…）と同じ分け方の項目は二重になるので使わない。
 *    1 つの選択肢に 85% 以上が偏る項目（入力端子がほぼ USB-C 等）も選ぶ意味が無いので使わない
 * 3) 当てはまる項目が無ければカードは出さない（件数と「ほかの条件」の行だけ） */
const FX_PRIMARY = {
  cable: "長さ", // 端子の種類はサブカテゴリの行と同じ分け方。長さは 1 商品に複数の長さがあるので自動の条件から外れるが、ケーブルを選ぶ軸として指定する
  wirelesscharger: "充電タイプ ＆ 機能",
  powerstrip: null, // 選ぶ軸（口数・タイプ）のデータがまだ無い
  powerhouse: null, // 容量・出力のデータが 4 割弱
  "powerhouse-set": null,
  dockingstation: null, // 種類はほぼドッキングステーションで決まっている
};
// 見比べ用に表記ゆれを落とす（空白・記号・「ケーブル」「型」・括弧の補足）
const fxNorm = (t) => String(t).replace(/[(（][^)）]*[)）]/g, "").replace(/ケーブル|型|[\s&＆・/／,、-]/g, "").toLowerCase();
const fxPickPrimary = (blocks) => {
  const handle = (window.location.pathname.match(/^\/collections\/([^/]+)/) || [])[1];
  const cands = blocks.filter((b) => b.classList.contains("filter-v2-collection-category"));
  if (handle && handle in FX_PRIMARY) return FX_PRIMARY[handle] && cands.find((b) => fxTitle(b) === FX_PRIMARY[handle]) || null;
  const counts = readFilterCounts();
  const total = Number(getCountContainer()?.getAttribute("data-product-count") ?? 0);
  if (!counts || total < 4) return null;
  const tabs = [...document.querySelectorAll(".collection-categories__text")].map((t) => fxNorm(t.textContent)).filter((t) => t.length > 1);
  return (
    cands.find((b) => {
      const ins = fxInputs(b);
      if (!ins.length) return false;
      const c = counts[ins[0].name.replace(/\.color$/, "")] || {};
      const ns = ins.map((i) => c[i.value] || 0).filter((n) => n > 0);
      const sum = ns.reduce((a, n) => a + n, 0);
      if (!(ns.length >= 2 && sum >= total * 0.7 && sum <= total * 1.15 && Math.max(...ns) <= sum * 0.85)) return false;
      const liveLabels = ins.filter((i) => (c[i.value] || 0) > 0).map((i) => fxNorm(fxShort(fxLabel(i))));
      return liveLabels.filter((l) => tabs.some((t) => t.includes(l) || l.includes(t))).length < 2;
    }) || null
  );
};

const fxInit = () => {
  const form = elements.form;
  const inner = getCountContainer();
  if (!form || !inner) return;
  const blocks = fxBlocks();
  if (!blocks.length) return;
  fx.primary = fxPickPrimary(blocks);
  fx.primary?.classList.add("is-fx-primary");

  fx.bar = document.createElement("div");
  fx.bar.className = "fx-bar";
  fx.bar.setAttribute("role", "toolbar");
  fx.bar.setAttribute("aria-label", "絞り込みの項目");
  fx.main = document.createElement("div");
  fx.main.className = "fx-main";
  if (!fx.primary) fx.main.hidden = true;
  fx.row = document.createElement("div");
  fx.row.className = "fx-row";
  // 一覧（差し替わる部分）の外に置く
  inner.before(fx.bar, fx.main, fx.row);
  elements.filterUnit?.classList.add("is-fx-c");
  const fxMini = () => { if (fx.row) fx.bar.classList.toggle("is-on", isMobile() && fx.row.getBoundingClientRect().bottom < (parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--fx-top")) || 0) + 4); };
  window.addEventListener("scroll", fxMini, { passive: true });
  document.body.classList.add("has-fx-bar");

  fx.foot = document.createElement("div");
  fx.foot.className = "fx-pop-ft";
  elements.filterUnit?.appendChild(fx.foot);

  document.addEventListener("click", (e) => {
    if (!isMobile()) return;
    if (e.target.closest("[data-fx-all]")) {
      displayModal();
      fxSyncSort();
      return;
    }
    const stockBtn = e.target.closest("[data-fx-stock]");
    if (stockBtn) {
      const t = getElement("filter-v2-stock-toggle");
      if (t) { t.checked = !t.checked; stockBtn.setAttribute("aria-checked", t.checked); t.dispatchEvent(new Event("change", { bubbles: true })); }
      fxSyncSort();
      return;
    }
    const sb = e.target.closest("[data-fx-sort]");
    if (sb && elements.productSortDom) {
      elements.productSortDom.value = sb.dataset.fxSort;
      elements.productSortDom.dispatchEvent(new Event("change", { bubbles: true }));
      fxSyncSort();
      return;
    }
    const chip = e.target.closest("[data-fx-block]");
    if (chip) {
      const block = fxBlocks().find((b) => (b.id || fxTitle(b)) === chip.dataset.fxBlock);
      if (!block) return;
      if (fx.openBlock === block) fxClosePop();
      else {
        chip.scrollIntoView({ inline: "nearest", block: "nearest" });
        fxOpenPop(block);
      }
      return;
    }
    const pill = e.target.closest("[data-fx-pill]");
    if (pill && fx.primary) {
      const input = fxInputs(fx.primary)[Number(pill.dataset.fxPill)];
      if (!input || input.disabled) return;
      if (input.type === "radio" && input.checked) input.checked = false;
      else input.checked = !input.checked;
      fxClosePop();
      updateClearLinkVisibility();
      executeFilterSearch(elements.form);
      fxRender();
      return;
    }
    if (e.target.closest("[data-fx-close]")) {
      fxClosePop();
      return;
    }
    if (e.target.closest("[data-fx-reset]")) {
      if (fx.openBlock) fxResetBlock(fx.openBlock);
      return;
    }
    // 小窓の外を押したら閉じる
    if (fx.openBlock && !e.target.closest("#filter-v2-unit")) fxClosePop();
  });
  // 一覧を指でスクロールしたら閉じる（一覧が入れ替わって高さが変わるだけでは閉じない）
  let touchY = null;
  document.addEventListener("touchstart", (e) => { touchY = e.touches[0].clientY; }, { passive: true });
  document.addEventListener(
    "touchmove",
    (e) => {
      if (!fx.openBlock || touchY === null || e.target.closest("#filter-v2-unit, .fx-bar")) return;
      if (Math.abs(e.touches[0].clientY - touchY) > 24) fxClosePop();
    },
    { passive: true }
  );
  window.addEventListener("scroll", fxTrackTop, { passive: true });
  window.addEventListener("resize", () => {
    if (!isMobile()) fxClosePop();
    fxSetTop();
  });
  fxSetTop();
  fxRender();
};

/* ========================================
 * 初期化
 * ======================================== */

const initialize = () => {
  elements = getElements();
  if (!elements.form) return;

  if (elements.productSortDom) {
    elements.productSortDom.addEventListener("change", handleSortChange);
  }

  if (elements.filterBtn) {
    elements.filterBtn.addEventListener("click", displayModal);
  }

  if (elements.prevBtn) {
    elements.prevBtn.addEventListener("click", () => closeModal(true));
  }

  if (elements.applyBtn) {
    elements.applyBtn.addEventListener("click", () => {
      executeFilterSearch(elements.form);
      closeModal(false);
    });
  }

  elements.form.addEventListener("change", handleFilterChange);
  elements.form.addEventListener("input", handleFilterChange);
  elements.form.addEventListener("mousedown", handleRadioMousedown);
  elements.form.addEventListener("click", handleRadioClick);

  elements.form.addEventListener("submit", (e) => {
    e.preventDefault();
    executeFilterSearch(elements.form);
  });

  elements.form.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      executeFilterSearch(elements.form);
    }
  });

  document.addEventListener("click", handleClearLinkClick);

  document.addEventListener("click", (e) => {
    const chip = e.target.closest(".filter-v2-chip");
    if (!chip) return;
    e.preventDefault();
    const url = chip.getAttribute("data-chip-url");
    if (url) {
      showLoading();
      fetchAndReplace(url);
    }
  });

  setInitialValues();
  fxInit();
  applyFilterAvailability(readFilterCounts());
  initPriceSlider();
  initStockToggle();
  initAccordion();
  renderActiveChips();
  hideFeaturedProducts();
  updateClearLinkVisibility();
  applyDefaultStockFilter();

  window.addEventListener("popstate", () => {
    showLoading();
    fetchAndReplace(window.location.href, { skipPushState: true });
  });

  const productsContainer = getElement("collection-products-v2");
  if (productsContainer) {
    productsContainer.addEventListener("click", (e) => {
      const link = e.target.closest("a[href]");
      if (!link || link.target === "_blank" || link.hasAttribute("download")) return;
      if (link.classList.contains("filter-v2-link-clear") ||
          link.classList.contains("filter-v2-link-clear-pc")) return;
      const href = link.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        if (url.pathname !== window.location.pathname) return;
        e.preventDefault();
        showLoading();
        const isPagination = !!link.closest(".pagination");
        fetchAndReplace(url.toString()).then(() => {
          if (isPagination) {
            const target = document.querySelector(".collection-products-v2-list") ||
                           document.querySelector(".collection-products-v2-inner");
            if (target) {
              var headerOffset = 160;
              var top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
              window.scrollTo({ top: top, behavior: "smooth" });
            }
          }
        });
      } catch {
        /* 無効なURLは通常遷移に任せる */
      }
    });
  }
};

document.addEventListener("DOMContentLoaded", initialize);
